import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock3, LockKeyhole, MessageCircle, Wrench, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import MaintenanceCountdown from "@/components/MaintenanceCountdown";
import "@/components/site-status.css";

const PARTICLES = Array.from({ length: 18 }, (_, i) => ({
  left: `${(i * 53) % 100}%`,
  duration: `${9 + (i % 6) * 2}s`,
  delay: `${-(i * 1.3)}s`,
  size: 3 + (i % 3),
}));

export default function MaintenancePage({ data, preview = false, supportWhatsApp = "" }) {
  const { user, logout } = useAuth() || {};
  const title = data?.title || "Nous revenons très vite";
  const message = data?.message || "Le site est actuellement en maintenance.";
  const waHref = supportWhatsApp ? `https://wa.me/${supportWhatsApp.replace(/\D/g, "")}` : "";

  return (
    <div className={`mt-root ${preview ? "mt-preview" : ""}`} data-testid="maintenance-page">
      <div className="mt-stars" />
      <div className="mt-orb mt-orb-1" />
      <div className="mt-orb mt-orb-2" />
      <div className="mt-orb mt-orb-3" />
      <div className="mt-floor" />
      <div className="mt-scan" />
      <div className="mt-noise" />
      {PARTICLES.map((p, i) => (
        <span key={i} className="mt-particle" style={{ left: p.left, animationDuration: p.duration, animationDelay: p.delay, width: p.size, height: p.size }} />
      ))}

      <div className="mt-content">
        <motion.div initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: "easeOut" }} className="mt-ring">
          <div className="mt-ring-core">
            <img src="/logo-mark.png" alt="ISMO-TECH" className="h-11 w-auto" style={{ filter: "drop-shadow(0 0 14px rgba(56,189,248,.7))" }} />
          </div>
        </motion.div>

        <motion.span initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.6 }} className="mt-badge mt-8" data-testid="maintenance-badge">
          <span className="mt-dot" /> <Wrench className="h-3.5 w-3.5" /> Maintenance en cours
        </motion.span>

        <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }}
          className="mt-title mt-5 max-w-3xl text-4xl sm:text-5xl lg:text-6xl" data-testid="maintenance-title">
          {title}
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.7 }}
          className="mt-5 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base" data-testid="maintenance-message">
          {message}
        </motion.p>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.6 }} className="mt-8 flex w-full flex-col items-center gap-4">
          <div className="mt-bar" />
          {data?.eta_at && <MaintenanceCountdown target={data.eta_at} />}
          {data?.eta && (
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold text-slate-200 backdrop-blur" data-testid="maintenance-eta">
              <Clock3 className="h-3.5 w-3.5 text-cyan-300" /> {data.eta}
            </span>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.6 }} className="mt-9 flex flex-wrap items-center justify-center gap-3">
          {data?.show_contact !== false && waHref && (
            <a href={waHref} target="_blank" rel="noreferrer" className="mt-btn-primary" data-testid="maintenance-contact">
              <MessageCircle className="h-4 w-4" /> Nous contacter sur WhatsApp
            </a>
          )}
          {preview ? (
            <span className="mt-btn-ghost"><LockKeyhole className="h-4 w-4" /> Connexion administrateur</span>
          ) : user && user.role !== "admin" ? (
            <button type="button" onClick={() => logout?.()} className="mt-btn-ghost" data-testid="maintenance-logout">
              <LogOut className="h-4 w-4" /> Se déconnecter
            </button>
          ) : (
            <Link to="/connexion" className="mt-btn-ghost" data-testid="maintenance-login">
              <LockKeyhole className="h-4 w-4" /> Connexion administrateur
            </Link>
          )}
        </motion.div>

        <p className="mt-12 text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">ISMO-TECH · Multiples Services Informatiques</p>
      </div>
    </div>
  );
}
